<template>
    <b-modal
        :visible="show"
        @change="show = $event"
        size="xl"
        centered
        scrollable
        no-close-on-backdrop
        no-close-on-esc
        hide-footer
        :title="$t('TechnicalData')">
        <b-container fluid class="p-0">
            <!-- TOOLBAR : MODELE D'IMPORT ET IMPORT DES DONNEES TECHNIQUES PAR FICHIER -->
            <b-row class="mb-2">
                <b-col class="d-flex justify-content-end">
                    <b-button
                        @click="downloadTechDataTemplate"
                        :variant="'outline-' + Variant"
                        size="sm"
                        pill
                        class="mx-1"
                        :disabled="isImporting || isDownloadingTemplate">
                        <font-awesome-icon icon="fa-solid fa-download" class="mr-1"/>
                        {{ $t('TechDataTemplate') }}
                    </b-button>
                    <b-button
                        @click="$refs.techDataFileInput.click()"
                        :variant="'outline-' + Variant"
                        size="sm"
                        pill
                        class="mx-1"
                        :disabled="isImporting || isDownloadingTemplate">
                        <font-awesome-icon icon="fa-solid fa-file-import" class="mr-1"/>
                        {{ $t('ImportTechData') }}
                    </b-button>
                    <input
                        ref="techDataFileInput"
                        type="file"
                        accept=".xlsx"
                        style="display: none"
                        @change="onTechDataFileSelected"/>
                </b-col>
            </b-row>
            <b-alert
                :show="importErrors.length > 0"
                variant="danger"
                dismissible
                @dismissed="importErrors = []"
                class="mb-3">
                <div v-for="(error, index) in importErrors" :key="index" class="small">{{ error }}</div>
            </b-alert>
            <!-- LISTE DES COMPTEURS DU MANDAT -->
            <b-table
                :items="meters"
                :fields="fields"
                :thead-class="'bg-' + Variant + ' text-white text-center'"
                small
                outlined
                responsive
                show-empty
                tbody-tr-class="align-middle text-center"
                :empty-text="$t('NoMetersAvailable')">
                <template #cell(Name)="row">
                    {{ row.item.Informations.MeterName }}
                </template>
                <template #cell(RaepceNumber)="row">
                    {{ row.item.RaepceNumber }}
                </template>
                <template #cell(Address)="row">
                    {{ row.item.Informations.Address }}
                </template>
                <template #cell(Segments)="row">
                    {{ getSegments(row.item).join(' / ') || '-' }}
                </template>
                <template #cell(TariffCode)="row">
                    {{ getTariffCode(row.item) || '-' }}
                </template>
                <template #cell(Actions)="row">
                    <b-button
                        @click="selectMeter(row.item)"
                        :id="getMeterAnchorId(row.item)"
                        :variant="'outline-' + Variant"
                        class="btn-circle border-0"
                        pill
                        v-b-tooltip.bottom
                        :title="$t('EnterTechnicalData')"
                        size="sm">
                        <font-awesome-icon icon="fa-solid fa-pen" />
                    </b-button>
                </template>
            </b-table>
        </b-container>

        <!-- PETITE FENETRE DE SAISIE DES DONNEES TECHNIQUES DU COMPTEUR SELECTIONNE -->
        <!-- Les données sont stockées au format ENEDIS (root/points/point) : chaque enregistrement ajoute un point à l'historique -->
        <b-modal
            v-model="editorVisible"
            size="lg"
            centered
            scrollable
            no-close-on-backdrop
            no-close-on-esc
            :title="editorTitle">
            <b-container fluid class="p-0">
                <!-- HISTORIQUE DES POINTS DU COMPTEUR -->
                <div v-if="meterHistory.length > 0" class="mb-3 border rounded p-2">
                    <h6 class="font-weight-bold mb-1">{{ $t('History') }}</h6>
                    <div v-for="(point, index) in meterHistory" :key="index" class="d-flex align-items-center small text-muted">
                        <div class="flex-grow-1">
                            {{ formatDate(point.DonneesGenerales?.DateDerniereModificationFormuleTarifaireAcheminement) }}
                            &mdash; {{ point.DonneesGenerales?.Segment?.Code || '-' }}
                            &mdash; {{ point.SituationContractuelle?.StructureTarifaire?.FormuleTarifaireAcheminement?.Code || '-' }}
                        </div>
                        <b-button
                            @click="editHistoryPoint(index)"
                            :variant="'outline-' + Variant"
                            size="sm"
                            pill
                            class="btn-circle border-0 ml-2"
                            v-b-tooltip.bottom
                            :title="$t('ModifyPoint')">
                            <font-awesome-icon icon="fa-solid fa-pen" />
                        </b-button>
                    </div>
                </div>

                <!-- NOUVEAUX POINTS A CREER : SAISIE MULTIPLE AVANT ENREGISTREMENT -->
                <div v-if="newPoints.length > 0" class="mb-3 border rounded p-2">
                    <h6 class="font-weight-bold mb-1">{{ $t('NewPoints') }}</h6>
                    <div v-for="(point, index) in newPoints" :key="'new-' + index" class="d-flex align-items-center small text-muted">
                        <div class="flex-grow-1">
                            {{ formatDate(point.DonneesGenerales?.DateDerniereModificationFormuleTarifaireAcheminement) }}
                            &mdash; {{ point.DonneesGenerales?.Segment?.Code || '-' }}
                            &mdash; {{ point.SituationContractuelle?.StructureTarifaire?.FormuleTarifaireAcheminement?.Code || '-' }}
                        </div>
                        <b-button
                            @click="removePoint(index)"
                            variant="outline-danger"
                            size="sm"
                            pill
                            class="btn-circle border-0 ml-2"
                            v-b-tooltip.bottom
                            :title="$t('RemovePoint')">
                            <font-awesome-icon icon="fa-solid fa-xmark" />
                        </b-button>
                    </div>
                </div>

                <!-- CHAMPS DU FORMULAIRE : GENERES DEPUIS L'ARCHITECTURE DECLAREE COTE SERVEUR -->
                <!-- (SMAAttribute du manager + SMAFormField sur les propriétés de la classe de données techniques) -->
                <b-form-group v-for="field in visibleTechnicalDataFields" :key="field.Path" :label="$t(field.LabelKey)" label-class="font-weight-bold">
                    <!-- Boutons (ex : segment Cx et/ou Px) : recliquer sur la valeur active la désélectionne -->
                    <template v-if="field.Type == 'buttons'">
                        <div class="d-flex flex-wrap" role="group">
                            <b-button
                                v-for="option in getFieldOptions(field)" :key="option"
                                :pressed="isOptionSelected(field, option)"
                                :variant="isOptionSelected(field, option) ? Variant : 'outline-' + Variant"
                                @click="toggleFieldValue(field, option)"
                                class="mr-1 mb-1">
                                {{ option }}
                            </b-button>
                        </div>
                        <b-form-invalid-feedback :state="!field.Required || hasFieldValue(fieldValues[field.Path]) || !touchedFields[field.Path]" class="d-block mt-1">
                            {{ $t('Required') }}
                        </b-form-invalid-feedback>
                    </template>
                    <!-- Liste (ex : formule tarifaire, calendrier fournisseur) -->
                    <b-form-select v-else-if="field.Type == 'select'" v-model="fieldValues[field.Path]" :options="getFieldOptions(field)"/>
                    <!-- Date -->
                    <b-form-input v-else-if="field.Type == 'date'" v-model="fieldValues[field.Path]" type="date"/>
                    <!-- Plage horaire (ex : heures creuses) au format (00h00-00h00), plusieurs plages séparées par des points-virgules -->
                    <template v-else-if="field.Type == 'hoursRange'">
                        <b-form-input v-model="fieldValues[field.Path]" placeholder="(00h00-00h00)" :state="hoursRangeState(field)" v-mask="hoursRangeMask"/>
                        <b-form-invalid-feedback :state="hoursRangeState(field)" class="d-block mt-1">
                            {{ $t('InvalidHoursRange') }}
                        </b-form-invalid-feedback>
                    </template>
                    <!-- Texte -->
                    <b-form-input v-else v-model="fieldValues[field.Path]" :placeholder="$t(field.LabelKey)"/>
                </b-form-group>
            </b-container>
            <template #modal-footer>
                <b-button @click="cancelEdition" :variant="'outline-' + Variant" pill class="mr-2">
                    {{ $t('Return') }}
                </b-button>
                <b-button v-if="editedHistoryIndex == null && (hasPendingPoints || isFormValid)" @click="addPoint" :variant="'outline-' + Variant" pill class="mr-2" :disabled="!isFormValid || isSending">
                    {{ $t('AddPoint') }}
                    <font-awesome-icon icon="fa-solid fa-plus" class="ml-2"/>
                </b-button>
                <b-button @click="saveTechnicalData" :variant="Variant" pill :disabled="(!hasPendingPoints && !isFormValid) || isSending">
                    <span v-if="isSending">
                        <b-spinner small></b-spinner> {{ $t('Send') }}...
                    </span>
                    <span v-else>
                        {{ $t('Save') }}
                    </span>
                </b-button>
            </template>
        </b-modal>
    </b-modal>
</template>
<script lang="ts">

    /**
     * Composants Vue js
     */
    import { Component, InjectReactive, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
    import { mixins } from 'vue-class-component';
    import { Getter } from 'vuex-class';

    /**
     * Classes et énumérations
     */
    import { SMACertificate } from '@/types/Administration/SMA/SMACertificate';
    import { Meter } from '@/types/Administration/SMA/MetersInformations';
    import { SMADistributorForm } from '@/types/Administration/SMA/SMADistributorForm';
    import Dictionary from '@/types/Dictionary';

    /**
     * Utilitaire
     */
    import { SMAUtils } from './SMAUtils';

    /**
     * Traductions
     */
    import locale from '../locales/SMA.json';

    @Component({
        i18n: { messages: locale }
    })
    /**
     * Composant de saisie des données techniques des compteurs d'un mandat
     * Les données sont stockées au format ENEDIS (root/points/point) avec historique de points
     * Accessible aux superviseurs une fois le mandat signé par le superviseur
     */
    export default class TechnicalDataForm extends mixins(Vue, SMAUtils) {

        /**
         * Affichage de la fenêtre
         */
        @PropSync('Show', { default: false })
        private show!: boolean;

        /**
         * Mandat selectionné
         */
        @Prop()
        private Certificate!: SMACertificate;

        /**
         * Variant de couleur à appliquer sur le composant
         */
        @InjectReactive()
        private Variant!: string;

        /**
         * Liste des codes tarifaires pour les données techniques des compteurs
         */
        @Getter('CertificateStore/GetTariffCodes')
        private tariffCodes!: Array<any>;

        /**
         * Liste des formulaires distributeurs
         */
        @Getter('CertificateStore/GetForms')
        private forms!: Dictionary<SMADistributorForm>;

        /**
         * Segments sélectionnables (C et P, un seul segment par point au format ENEDIS)
         */
        private readonly segments: Array<string> = ['C1', 'C2', 'C4', 'C5', 'P1', 'P2', 'P3', 'P4'];

        /**
         * Format d'une plage horaire : (00h00-00h00), plusieurs plages séparées par des points-virgules
         */
        private static readonly HOURS_RANGE_PATTERN: RegExp = /^\s*(?:hc\s*)?\(?\s*(?:[01]?\d|2[0-3])h[0-5]\d\s*-\s*(?:[01]?\d|2[0-3])h[0-5]\d\s*\)?(?:\s*;\s*(?:hc\s*)?\(?\s*(?:[01]?\d|2[0-3])h[0-5]\d\s*-\s*(?:[01]?\d|2[0-3])h[0-5]\d\s*\)?)*\s*$/i;

        /**
         * Masque de saisie d'une heure (HHhMM) : heures limitées à 23, minutes à 59
         * @param digits Chiffres déjà saisis pour cette heure (HHMM)
         */
        private static hoursMask(digits: string): Array<any>
        {
            const hours = [/[0-2]/, digits.charAt(0) == '2' ? /[0-3]/ : /[0-9]/];
            const minutes = [/[0-5]/, /[0-9]/];

            return [...hours, 'h', ...minutes];
        }

        /**
         * Masque de saisie des plages heures creuses, adapté à la valeur en cours :
         * chaque plage (HHhMM-HHhMM) est suivie, si la saisie continue, d'un point-virgule et d'une nouvelle plage
         * Le préfixe hc éventuel des valeurs existantes est conservé pour ne pas les altérer à l'affichage
         * Fonction masque interprétée par la directive v-mask
         * @param value Valeur courante du champ
         */
        private hoursRangeMask(value: string): Array<any>
        {
            const raw = String(value ?? '');
            const digits = raw.replace(/[^0-9]/g, '');
            const prefix = /^\s*hc\b/i.test(raw) ? ['h', 'c', ' '] : [];
            const mask: Array<any> = [...prefix];

            let index = 0;

            do
            {
                const rangeDigits = digits.substring(index, index + 8);

                if (index > 0)
                    mask.push(';');

                mask.push('(',
                    ...TechnicalDataForm.hoursMask(rangeDigits.substring(0, 4)),
                    '-',
                    ...TechnicalDataForm.hoursMask(rangeDigits.substring(4, 8)),
                    ')');

                index += 8;
            }
            while (index < digits.length);

            return mask;
        }

        /**
         * Colonnes du tableau des compteurs
         */
        private fields: Array<any> = [
            { key: 'Name', label: this.$t('Meters').toString(), sortable: true },
            { key: 'RaepceNumber', label: this.$t('RaePceNumber').toString(), sortable: true },
            { key: 'Address', label: this.$t('Address').toString(), sortable: true },
            { key: 'Segments', label: this.$t('Segment').toString() },
            { key: 'TariffCode', label: this.$t('FormuleTarifaire').toString() },
            { key: 'Actions', label: '', class: 'text-center align-middle', thStyle: { width: '90px' } }
        ];

        /**
         * Compteur en cours d'édition
         */
        private selectedMeter: Meter | null = null;

        /**
         * Valeurs saisies pour les champs du formulaire, indexées par chemin de propriété
         */
        private fieldValues: any = {};

        /**
         * Points de données techniques en attente d'enregistrement (saisie multiple)
         */
        private newPoints: Array<any> = [];

        /**
         * Index du point d'historique remplacé par chaque point en attente (parallèle à newPoints) :
         * -1 quand le point en attente ne remplace aucun point existant (nouvelle date)
         */
        private newPointReplacedHistoryIndexes: Array<number> = [];

        /**
         * Index du point de l'historique en cours de modification (null : saisie d'un nouveau point)
         */
        private editedHistoryIndex: number | null = null;

        /**
         * Index du point de l'historique qui a servi de pré-remplissage au formulaire
         * (point courant à l'ouverture, point modifié via l'historique) :
         * l'enregistrement remplace ce point, il ne doit pas être compté comme doublon de date
         */
        private prefillHistoryIndex: number | null = null;

        /**
         * Champs touchés (pour l'affichage des messages d'erreur)
         */
        private touchedFields: any = {};

        /**
         * Indicateur d'envoi vers le serveur
         */
        private isSending: boolean = false;

        /**
         * Indicateur de téléchargement du modèle d'import
         */
        private isDownloadingTemplate: boolean = false;

        /**
         * Indicateur d'import des données techniques par fichier
         */
        private isImporting: boolean = false;

        /**
         * Erreurs du dernier import des données techniques
         */
        private importErrors: Array<string> = [];

        /**
         * Compteurs du mandat
         */
        private get meters(): Array<Meter>
        {
            return this.Certificate?.MeterInformations?.Meters ?? [];
        }

        /**
         * Historique des points du compteur sélectionné (format ENEDIS)
         */
        private get meterHistory(): Array<any>
        {
            return this.selectedMeter?.TechnicalData?.History ?? [];
        }

        /**
         * Point courant d'un compteur (le plus récent de l'historique)
         * @param meter Compteur
         */
        private getCurrentPoint(meter: Meter): any
        {
            return meter.TechnicalData?.History?.[0] ?? null;
        }

        /**
         * Segments du point courant d'un compteur
         * @param meter Compteur
         */
        private getSegments(meter: Meter): Array<string>
        {
            const segment = this.getCurrentPoint(meter)?.DonneesGenerales?.Segment?.Code;
            return segment ? [segment] : [];
        }

        /**
         * Formule tarifaire du point courant d'un compteur
         * @param meter Compteur
         */
        private getTariffCode(meter: Meter): string
        {
            return this.getCurrentPoint(meter)?.SituationContractuelle?.StructureTarifaire?.FormuleTarifaireAcheminement?.Code ?? "";
        }

        /**
         * Champs du formulaire des données techniques pour le distributeur du mandat
         * Architecture déclarée côté serveur : SMAAttribute du manager + SMAFormField sur la classe de données techniques
         */
        private get technicalDataFields(): Array<any>
        {
            return this.forms[this.Certificate?.Distributor]?.TechnicalDataFields ?? [];
        }

        /**
         * Point en cours d'édition (point de l'historique modifié ou point courant du compteur)
         */
        private get editedPoint(): any
        {
            if (this.editedHistoryIndex != null)
                return this.meterHistory[this.editedHistoryIndex];

            return this.selectedMeter != null ? this.getCurrentPoint(this.selectedMeter) : null;
        }

        /**
         * Lecture des postes de la programmation horaire d'un point
         * Les postes sont recherchés dans tous les compteurs du dispositif de comptage
         * (la casse des codes P / HC est ignorée)
         * @param point Point de données techniques
         */
        private getPointPostes(point: any): Array<any>
        {
            const compteurs = point?.SituationComptage?.DispositifComptage?.Compteurs ?? [];

            for (const compteur of compteurs)
            {
                const postes = compteur?.ProgrammationHoraire;

                if (Array.isArray(postes) && postes.length > 0)
                    return postes;
            }

            return [];
        }

        /**
         * Lecture des périodes horaires du poste portant le code donné (casse ignorée)
         * @param postes Postes de la programmation horaire
         * @param code Code du poste (ex : P, HC)
         */
        private getPostePeriodes(postes: Array<any>, code: string): string
        {
            const poste = postes.find((poste: any) => String(poste?.Code ?? '').toLowerCase() == code.toLowerCase());

            return poste?.PeriodesHoraires ?? "";
        }

        /**
         * Indique si le point possède une pointe (poste P de la programmation horaire, ex : C2 / HTA)
         * Les heures creuses sont alors portées par le poste HC de la programmation horaire du compteur,
         * sinon par la plage heures creuses du relais (ex : C5 / BT)
         * Pour un nouveau point sans donnée, le mode suit la formule tarifaire (codes à 5 plages)
         */
        private get isPointeSchedule(): boolean
        {
            const postes = this.getPointPostes(this.editedPoint);

            if (this.getPostePeriodes(postes, 'P') != "")
                return true;

            const tariffField = this.technicalDataFields.find(field => field.LabelKey == 'FormuleTarifaire');
            const code = tariffField ? String(this.fieldValues[tariffField.Path] ?? "") : "";

            return code.trim().endsWith('5');
        }

        /**
         * Champs affichés : les périodes de pointe n'apparaissent que lorsque le point possède une pointe
         * (poste P de la programmation horaire) ou pour un tarif à 5 plages temporelles
         */
        private get visibleTechnicalDataFields(): Array<any>
        {
            return this.isPointeSchedule
                ? this.technicalDataFields
                : this.technicalDataFields.filter(field => field.LabelKey != 'PointePeriodesHoraires');
        }

        /**
         * Validité du formulaire : tous les champs obligatoires sont renseignés
         */
        private get isFormValid(): boolean
        {
            return this.technicalDataFields.every(field =>
                (!field.Required || this.hasFieldValue(this.fieldValues[field.Path]))
                && this.isFieldFormatValid(field));
        }

        /**
         * Validité du format d'un champ (ex : plage horaire 00h00-00h00)
         * @param field Champ du formulaire
         */
        private isFieldFormatValid(field: any): boolean
        {
            return field.Type != 'hoursRange' || this.hoursRangeState(field) != false;
        }

        /**
         * État de validation d'un champ de plage horaire : null si valide ou vide, false si le format est incorrect
         * @param field Champ du formulaire
         */
        private hoursRangeState(field: any): boolean | null
        {
            const value = String(this.fieldValues[field.Path] ?? "").trim();

            return value == "" || TechnicalDataForm.HOURS_RANGE_PATTERN.test(value) ? null : false;
        }

        /**
         * Indique si un champ est renseigné
         * Les champs multi-sélection (ex : segments) sont des tableaux de valeurs
         * @param value Valeur du champ
         */
        private hasFieldValue(value: any): boolean
        {
            return Array.isArray(value) ? value.length > 0 : (value ?? "") != "";
        }

        /**
         * Indique si une option d'un champ de type boutons est sélectionnée
         * @param field Champ du formulaire
         * @param option Option
         */
        private isOptionSelected(field: any, option: string): boolean
        {
            const value = this.fieldValues[field.Path];

            return Array.isArray(value) ? value.includes(option) : value == option;
        }

        /**
         * Indique si des points sont en attente d'enregistrement
         */
        private get hasPendingPoints(): boolean
        {
            return this.newPoints.length > 0;
        }

        /**
         * Segments sélectionnés dans le formulaire (un Cx et/ou un Px)
         */
        private get selectedSegments(): Array<string>
        {
            const segmentField = this.technicalDataFields.find(field => field.Options == 'Segments');

            if (!segmentField)
                return [];

            const value = this.fieldValues[segmentField.Path];

            return Array.isArray(value) ? value : String(value ?? "").split('/').filter(segment => segment != "");
        }

        /**
         * Options de la formule tarifaire : codes chargés depuis le serveur (GetSMAForms),
         * filtrés suivant le(s) segment(s) sélectionné(s) (tous si aucun segment n'est sélectionné)
         * Un code tarifaire peut porter plusieurs segments séparés par '/' (ex : BTSUP pour C1/C2/C4)
         * Le libellé porte le segment et la description du code tarifaire
         */
        private get tariffCodeOptions(): Array<{ value: string, text: string }>
        {
            const options = (this.tariffCodes ?? [])
                .filter(tariffCode => tariffCode?.Name
                    && (this.selectedSegments.length == 0 || this.selectedSegments.some(segment => String(tariffCode.Segment ?? '').split('/').includes(segment))))
                .map(tariffCode => ({
                    value: tariffCode.Name,
                    text: tariffCode.Name + (tariffCode.Segment ? ' (' + tariffCode.Segment + ')' : '') + ' - ' + (tariffCode.Description ?? '')
                }));

            return [{ value: "", text: this.$t('SelectATariffCode').toString() }, ...options];
        }

        /**
         * Affichage de la petite fenêtre d'édition des données techniques
         */
        private get editorVisible(): boolean
        {
            return this.selectedMeter != null;
        }

        /**
         * Titre de la fenetre d'edition : nom du compteur et RAE / PCE
         */
        private get editorTitle(): string
        {
            const meter = this.selectedMeter;

            return meter ? meter.Informations.MeterName + ' (' + meter.RaepceNumber + ')' : '';
        }

        private set editorVisible(value: boolean)
        {
            if (!value)
                this.selectedMeter = null;
        }

        /**
         * Formatage d'une date pour l'affichage de l'historique
         * @param value Date au format string
         */
        private formatDate(value: string): string
        {
            const date = new Date(value);

            if (isNaN(date.getTime()))
                return '-';

            return date.toLocaleDateString('fr-FR');
        }

        /**
         * Sélection / désélection d'un champ de type boutons :
         * recliquer sur la valeur active la désélectionne
         * Les champs multi-sélection (ex : segments) acceptent une valeur par groupe :
         * un Cx et un Px peuvent être sélectionnés simultanément, une valeur d'un même
         * groupe remplace la précédente
         * @param field Champ du formulaire
         * @param option Valeur sélectionnée
         */
        private toggleFieldValue(field: any, option: string): void
        {
            this.$set(this.touchedFields, field.Path, true);

            const currentValue = this.fieldValues[field.Path];

            if (Array.isArray(currentValue))
            {
                const values: Array<string> = [...currentValue];
                const group = option.charAt(0);
                const index = values.indexOf(option);

                if (index >= 0)
                    values.splice(index, 1);
                else
                {
                    const sameGroupIndex = values.findIndex(value => value.charAt(0) == group);

                    if (sameGroupIndex >= 0)
                        values.splice(sameGroupIndex, 1, option);
                    else
                        values.push(option);
                }

                this.$set(this.fieldValues, field.Path, values);
            }
            else
                this.$set(this.fieldValues, field.Path, currentValue == option ? "" : option);

            // Un changement de segment filtre les codes tarifaires : la sélection incompatible est désélectionnée
            if (field.Options == 'Segments')
                this.clearIncompatibleTariffCode();
        }

        /**
         * Désélection du code tarifaire courant s'il n'est plus proposé pour le(s) segment(s) sélectionné(s)
         */
        private clearIncompatibleTariffCode(): void
        {
            const tariffField = this.technicalDataFields.find(field => field.Options == 'TariffCodes' && field.Type == 'select');

            if (!tariffField || !this.hasFieldValue(this.fieldValues[tariffField.Path]))
                return;

            if (!this.tariffCodeOptions.some(option => option.value == this.fieldValues[tariffField.Path]))
                this.$set(this.fieldValues, tariffField.Path, "");
        }

        /**
         * Options d'un champ du formulaire (Segments ou TariffCodes)
         * @param field Champ du formulaire
         */
        private getFieldOptions(field: any): Array<any>
        {
            if (field.Options == 'Segments')
                return this.segments;

            if (field.Options == 'TariffCodes')
                return this.tariffCodeOptions;

            return [];
        }

        /**
         * Lecture d'une valeur dans un objet à partir de son chemin de propriété
         * @param obj Objet
         * @param path Chemin (ex : DonneesGenerales.Segment.Code)
         */
        private getPathValue(obj: any, path: string): any
        {
            // Les segments de liste (notation []) et les tableaux existants sont lus sur le premier élément
            return path.split('.').reduce((acc, part) =>
            {
                const key = part.endsWith('[]') ? part.substring(0, part.length - 2) : part;
                const value = acc == null ? undefined : acc[key];

                return Array.isArray(value) ? value[0] : value;
            }, obj);
        }

        /**
         * Écriture d'une valeur dans un objet à son chemin de propriété (crée les niveaux intermédiaires)
         * @param obj Objet
         * @param path Chemin (ex : DonneesGenerales.Segment.Code)
         * @param value Valeur
         */
        private setPathValue(obj: any, path: string, value: any): void
        {
            const parts = path.split('.');
            let current = obj;

            for (let i = 0; i < parts.length - 1; i++)
            {
                const isList = parts[i].endsWith('[]');
                const key = isList ? parts[i].substring(0, parts[i].length - 2) : parts[i];

                // Segment de liste (notation []) : premier élément du tableau, créé si absent
                if (isList)
                {
                    if (!Array.isArray(current[key]))
                        this.$set(current, key, []);

                    if (current[key].length == 0)
                        current[key].push({});

                    current = current[key][0];
                }
                else
                {
                    if (!current[key])
                        current[key] = {};

                    current = current[key];
                }
            }

            current[parts[parts.length - 1]] = value;
        }

        /**
         * Sélection d'un compteur pour la saisie / modification de ses données techniques
         * Pré-remplissage depuis le point courant du compteur
         * @param meter Compteur
         */
        private selectMeter(meter: Meter): void
        {
            this.selectedMeter = meter;
            this.newPoints = [];
            this.newPointReplacedHistoryIndexes = [];
            this.editedHistoryIndex = null;
            this.prefillHistoryIndex = this.getCurrentPoint(meter) != null ? 0 : null;

            this.initializeFieldValues(this.getCurrentPoint(meter));
        }

        /**
         * Modification d'un point de données techniques déjà enregistré
         * Le formulaire est pré-rempli depuis le point ; à l'enregistrement,
         * le point de l'historique portant la même date est remplacé côté serveur
         * @param index Index du point dans l'historique
         */
        private editHistoryPoint(index: number): void
        {
            this.editedHistoryIndex = index;
            this.prefillHistoryIndex = index;

            this.initializeFieldValues(this.meterHistory[index]);
        }

        /**
         * Initialisation des valeurs du formulaire depuis un point de données techniques
         * Les objets sont construits complets avant affectation : des clés dynamiques ajoutées
         * après coup sur un objet réactif ne seraient pas observées par Vue
         * @param point Point de pré-remplissage (null pour une saisie vierge)
         */
        private initializeFieldValues(point: any): void
        {
            const values: any = {};
            const touched: any = {};

            for (const field of this.technicalDataFields)
            {
                let value = this.getPathValue(point, field.Path) ?? "";

                // Champs multi-sélection (ex : segments Cx / Px) : valeur en tableau de codes
                if (field.Type == 'buttons' && field.Options == 'Segments')
                    value = String(value).split('/').filter(v => v != "");
                else if (field.Type == 'date')
                    value = value != "" ? String(value).substring(0, 10) : new Date().toISOString().substring(0, 10);

                values[field.Path] = value;
                touched[field.Path] = false;
            }

            // Périodes de pointe : périodes horaires du poste P de la programmation horaire du compteur
            // Heures creuses : poste HC de la programmation horaire pour un tarif à 5 plages,
            // plage du relais pour un tarif à 2/4 plages (l'autre source sert de repli)
            const postes = this.getPointPostes(point);
            const pointeField = this.technicalDataFields.find(field => field.LabelKey == 'PointePeriodesHoraires');

            if (pointeField)
                values[pointeField.Path] = this.getPostePeriodes(postes, 'P');

            const hcField = this.technicalDataFields.find(field => field.LabelKey == 'PlagesHeuresCreuses' || field.LabelKey == 'PlageHeuresCreuses');

            if (hcField)
            {
                const posteHCPeriodes = this.getPostePeriodes(postes, 'HC');
                const relaisPeriodes = String(values[hcField.Path] ?? "");

                // Point avec poste P (ex : C2) : les heures creuses viennent du poste HC de la programmation horaire
                // Sinon (ex : C5) : elles viennent de la plage heures creuses du relais, le poste HC servant de repli
                values[hcField.Path] = this.getPostePeriodes(postes, 'P') != ""
                    ? posteHCPeriodes
                    : (relaisPeriodes != "" ? relaisPeriodes : posteHCPeriodes);
            }

            this.fieldValues = values;
            this.touchedFields = touched;

            // Trace de diagnostic : visible dans la console du navigateur (F12) a l'ouverture d'un compteur
            console.info('[Donnees techniques] mode=' + (this.isPointeSchedule ? 'pointe (postes P/HC)' : 'relais')
                + ', postes=' + JSON.stringify(postes.map((poste: any) => poste?.Code))
                + ', HC=' + JSON.stringify(hcField ? values[hcField.Path] : ''));
        }

        /**
         * Défilement du corps de la fenêtre vers l'élément donné
         * Le scrollTop est calculé explicitement : scrollIntoView n'est pas fiable dans une modale scrollable
         * @param element Élément cible
         */
        private scrollToElement(element: HTMLElement | null): void
        {
            if (!element)
                return;

            const container = element.closest('.modal-body') as HTMLElement | null;

            if (container)
            {
                container.scrollTop += element.getBoundingClientRect().top - container.getBoundingClientRect().top - 16;
                return;
            }

            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        /**
         * Identifiant de l'ancre DOM du compteur dans le tableau
         * @param meter Compteur
         */
        private getMeterAnchorId(meter: Meter): string
        {
            return 'meter-anchor-' + (meter.Informations.PersistentRef ?? '').replace(/[^a-zA-Z0-9_-]/g, '');
        }

        /**
         * Téléchargement du modèle d'import des données techniques du mandat
         * Le modèle contient une ligne par compteur, pré-remplie avec les données courantes
         */
        private downloadTechDataTemplate(): void
        {
            this.isDownloadingTemplate = true;

            this.$store.dispatch('CertificateStore/DownloadTechDataTemplate', {
                CertificateId: this.Certificate.CertificateId,
                Distributor: this.Certificate.Distributor
            }).then((result: any) => {
                if (!result?.success)
                    this.showToast("TechnicalData", "danger", result?.message);
            }).finally(() => {
                this.isDownloadingTemplate = false;
            });
        }

        /**
         * Sélection du fichier d'import des données techniques
         * @param event Evénement de sélection de fichier
         */
        private onTechDataFileSelected(event: any): void
        {
            const file: File = event?.target?.files?.[0];

            event.target.value = '';

            if (!file)
                return;

            this.importTechData(file);
        }

        /**
         * Import des données techniques des compteurs du mandat par fichier Excel
         * La date de dernière modification de la formule tarifaire est la clé d'un point :
         * une ligne portant une date déjà présente met à jour le point existant
         * @param file Fichier Excel issu du modèle
         */
        private async importTechData(file: File): Promise<void>
        {
            this.isImporting = true;
            this.importErrors = [];

            try
            {
                const result = await this.$store.dispatch('CertificateStore/ImportTechData', {
                    CertificateId: this.Certificate.CertificateId,
                    Distributor: this.Certificate.Distributor,
                    File: file
                });

                if (result?.success)
                {
                    // Mise à jour immédiate des compteurs modifiés avec les données techniques renvoyées
                    const technicalData = result.data?.technicalData;

                    if (technicalData)
                    {
                        for (const meter of this.meters)
                        {
                            if (technicalData[meter.RaepceNumber])
                                meter.TechnicalData = technicalData[meter.RaepceNumber];
                        }
                    }

                    // Rechargement des mandats : les données techniques sont renvoyées par le serveur
                    await this.$store.dispatch('CertificateStore/Load');

                    this.showToast("TechnicalData", "success", result.message);
                }
                else
                {
                    this.importErrors = result?.data?.Errors ?? [];

                    this.showToast("TechnicalData", "danger", result?.message ?? this.$t('UnknownError').toString());
                }
            }
            finally
            {
                this.isImporting = false;
            }
        }

        /**
         * Annulation de l'édition en cours
         */
        private cancelEdition(): void
        {
            this.editedHistoryIndex = null;
            this.prefillHistoryIndex = null;
            this.selectedMeter = null;
        }

        /**
         * Construction d'un point de données techniques depuis les valeurs saisies dans le formulaire
         */
        private buildPoint(): any
        {
            const point: any = { Id: this.selectedMeter?.RaepceNumber };

            for (const field of this.technicalDataFields)
            {
                // Les horaires horo-tarifaires (pointe / heures creuses) dépendent du type de tarif
                if (field.LabelKey == 'PointePeriodesHoraires' || field.LabelKey == 'PlagesHeuresCreuses' || field.LabelKey == 'PlageHeuresCreuses')
                    continue;

                const value = this.fieldValues[field.Path] ?? "";

                // Les champs multi-sélection (ex : segments Cx / Px) sont sérialisés en une seule valeur
                this.setPathValue(point, field.Path, Array.isArray(value) ? value.join('/') : value);
            }

            this.buildHoroSchedule(point);

            // Soutirage / injection déduits du segment si le formulaire en expose un
            const segmentField = this.technicalDataFields.find(field => field.Options == 'Segments');

            if (segmentField)
            {
                const segment = this.fieldValues[segmentField.Path] ?? "";
                const codes = Array.isArray(segment) ? segment : String(segment).split('/');
                point.Soutirage = codes.some(code => code.includes('C'));
                point.Injection = codes.some(code => code.includes('P'));
            }

            return point;
        }

        /**
         * Construction des horaires horo-tarifaires du point
         * Tarif à 5 plages temporelles : postes P (pointe) et HC (heures creuses) de la programmation horaire du compteur
         * Autres tarifs : plage heures creuses du relais
         * @param point Point en construction
         */
        private buildHoroSchedule(point: any): void
        {
            const pointeField = this.technicalDataFields.find(field => field.LabelKey == 'PointePeriodesHoraires');
            const hcField = this.technicalDataFields.find(field => field.LabelKey == 'PlagesHeuresCreuses' || field.LabelKey == 'PlageHeuresCreuses');

            if (!pointeField && !hcField)
                return;

            const pointeValue = pointeField ? String(this.fieldValues[pointeField.Path] ?? "").trim() : "";
            const hcValue = hcField ? String(this.fieldValues[hcField.Path] ?? "").trim() : "";

            if (this.isPointeSchedule)
            {
                if (pointeValue != "" || hcValue != "")
                {
                    const postes: Array<any> = [];

                    if (pointeValue != "")
                        postes.push({ Code: "P", Libelle: "Pointe", PeriodesHoraires: pointeValue });

                    if (hcValue != "")
                        postes.push({ Code: "HC", Libelle: "Heures Creuses", PeriodesHoraires: hcValue });

                    point.SituationComptage = { DispositifComptage: { Compteurs: [{ ProgrammationHoraire: postes }] } };
                }
            }
            else if (hcValue != "" && hcField)
            {
                this.setPathValue(point, hcField.Path, hcValue);
            }
        }

        /**
         * Ajout du point en cours de saisie à la liste des points à créer
         * puis réinitialisation du formulaire pour la saisie du point suivant
         */
        /**
         * Indique si plusieurs points partagent la même date de dernière modification de la formule tarifaire
         * (entre eux ou avec l'historique existant du compteur)
         * Les points d'historique remplacés par les points à enregistrer sont exclus du contrôle :
         * un point modifié porte la date du point qu'il remplace
         * @param points Points à vérifier
         */
        private hasDuplicateDate(points: Array<any>): boolean
        {
            const dateField = this.technicalDataFields.find(field => field.Type == 'date');

            if (!dateField)
                return false;

            const dateOf = (point: any) => String(this.getPathValue(point, dateField.Path) ?? '').substring(0, 10);

            const replacedIndexes = new Set(this.replacedHistoryIndexes);

            const history = this.meterHistory.filter((point, index) => !replacedIndexes.has(index));

            const allDates = [
                ...history.map(dateOf),
                ...points.map(dateOf)
            ];

            return allDates.some((date, index) => date != '' && allDates.indexOf(date) != index);
        }

        /**
         * Indexes des points d'historique remplacés par les points à enregistrer :
         * points déjà en attente (saisie multiple) et point en cours d'édition dans le formulaire
         */
        private get replacedHistoryIndexes(): Array<number>
        {
            const indexes = [...this.newPointReplacedHistoryIndexes];

            // Point en cours d'édition dans le formulaire : il n'est envoyé que si le formulaire est valide
            if (this.isFormValid && this.prefillHistoryIndex != null)
                indexes.push(this.prefillHistoryIndex);

            return indexes;
        }

        private addPoint(): void
        {
            if (!this.isFormValid)
                return;

            const point = this.buildPoint();

            // Unicité de la date de dernière modification de la formule tarifaire
            // (contre l'historique existant du compteur et les points déjà en attente)
            if (this.hasDuplicateDate([...this.newPoints, point]))
            {
                this.showToast("TechnicalData", "danger", this.$t('DuplicateTechnicalDataDate').toString());
                return;
            }

            this.newPoints.push(point);
            this.newPointReplacedHistoryIndexes.push(this.prefillHistoryIndex ?? -1);

            this.prefillHistoryIndex = null;
            this.initializeFieldValues(null);
        }

        /**
         * Suppression d'un point de la liste des points à créer
         * @param index Index du point à supprimer
         */
        private removePoint(index: number): void
        {
            this.newPoints.splice(index, 1);
            this.newPointReplacedHistoryIndexes.splice(index, 1);
        }

        /**
         * Enregistrement des données techniques du compteur :
         * les nouveaux points au format ENEDIS sont ajoutés en tête de l'historique côté serveur
         */
        private async saveTechnicalData(): Promise<void>
        {
            if (!this.selectedMeter)
                return;

            // Enregistrement de tous les points à créer : points déjà ajoutés et point en cours de saisie quand il est valide
            const points: Array<any> = [...this.newPoints];

            if (this.isFormValid)
                points.push(this.buildPoint());

            if (points.length == 0)
            {
                console.info('[Donnees techniques] enregistrement ignore : aucun point (formulaire invalide ?)');
                return;
            }

            // Unicité de la date de dernière modification de la formule tarifaire
            if (this.hasDuplicateDate(points))
            {
                console.info('[Donnees techniques] enregistrement BLOQUE : date en doublon', JSON.stringify(points.map((p: any) => this.getPathValue(p, "DonneesGenerales.DateDerniereModificationFormuleTarifaireAcheminement"))));
                this.showToast("TechnicalData", "danger",this.$t('DuplicateTechnicalDataDate').toString());
                return;
            }

            console.info('[Donnees techniques] champs du formulaire :', JSON.stringify(this.technicalDataFields.map((f: any) => f.LabelKey + '/' + f.Type)));
            console.info('[Donnees techniques] valeurs saisies :', JSON.stringify(this.fieldValues));
            console.info('[Donnees techniques] envoi de ' + points.length + ' point(s) au serveur :', JSON.stringify(points));

            const editedMeter = this.selectedMeter;

            this.isSending = true;

            const result = await this.$store.dispatch('CertificateStore/SaveTechnicalData', {
                CertificateId: this.Certificate.CertificateId,
                RaePceNumber: editedMeter.RaepceNumber,
                Points: points
            });

            console.info('[Donnees techniques] reponse serveur : ' + (result?.success ? 'SUCCES' : 'ERREUR : ' + result?.message));

            if (result?.success)
            {
                this.newPoints = [];
                this.newPointReplacedHistoryIndexes = [];

                // Mise a jour immediate du compteur affiche avec les donnees techniques renvoyees par le serveur
                if (result.data)
                    editedMeter.TechnicalData = result.data;

                // Rechargement des mandats : les donnees techniques saisies sont renvoyees par le serveur
                await this.$store.dispatch('CertificateStore/Load');

                // Si le rechargement n'a pas ramene les donnees techniques du compteur, on conserve la reponse du serveur
                const refreshedMeter = this.meters.find(meter => meter.RaepceNumber == editedMeter.RaepceNumber);

                if (result.data && refreshedMeter && !refreshedMeter.TechnicalData)
                    refreshedMeter.TechnicalData = result.data;

                this.showToast(result.message, "success", "TechnicalData");
                this.editedHistoryIndex = null;
                this.prefillHistoryIndex = null;
                this.selectedMeter = null;

                // Retour sur le compteur modifié dans la liste des compteurs
                this.$nextTick(() => {
                    const anchor = document.getElementById(this.getMeterAnchorId(editedMeter));
                    this.scrollToElement((anchor?.closest('tr') as HTMLElement) ?? null);
                });
            }
            else
            {
                this.showToast("TechnicalData", "danger", result?.message ?? this.$t('UnknownError').toString());
            }

            this.isSending = false;
        }

        /**
         * Réinitialisation de l'édition à la fermeture de la fenêtre
         * @param newVal Nouvel état d'affichage
         */
        @Watch('show')
        private onShowChanged(newVal: boolean): void
        {
            if (!newVal)
                this.selectedMeter = null;
        }
    }
</script>
<style scoped>
    /**
     * Libellés des en-têtes horizontaux, centrés verticalement dans la bande d'en-tête
     * (Bootstrap aligne par défaut les en-têtes en bas)
     */
    ::v-deep thead th {
        vertical-align: middle;
    }

    /**
     * Coins haut gauche et haut droite arrondis de l'en-tête du tableau
     */
    ::v-deep thead {
        border-radius: 8px 8px 0 0;
    }

    ::v-deep thead th:first-child {
        border-top-left-radius: 8px;
    }

    ::v-deep thead th:last-child {
        border-top-right-radius: 8px;
    }
</style>